import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Grid,
  Paper,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import EventIcon from "@mui/icons-material/Event";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 毛玻璃效果的卡片（无hover效果）
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 24,
    pointerEvents: "none",
  },
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "24px",
  padding: theme.spacing(4),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  // 移除hover效果
}));

// 毛玻璃效果的输入框，白色helperText
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255,255,255,0.87)",
    fontSize: "0.9rem",
    "&.Mui-focused": {
      color: "#fff",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    fontSize: "0.95rem",
  },
  "& .MuiFormHelperText-root": {
    color: "#fff",
    fontSize: "0.85rem",
    marginLeft: 0,
  },
  marginBottom: theme.spacing(3),
}));

// 毛玻璃效果的按钮 - 白色，无hover效果
const GlassButton = styled(Button)(({ theme, color = "#2196f3" }) => ({
  background: `linear-gradient(135deg, ${color}15, ${color}05)`,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  borderRadius: "8px",
  color: "#fff",
  padding: theme.spacing(1, 3),
  border: `1px solid ${color}20`,
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${color}15, ${color}05)`,
    transform: "none",
    boxShadow: "none",
    color: "#fff",
  },
  "&:disabled": {
    background: "rgba(255,255,255,0.08)",
    color: "#fff",
    opacity: 0.55,
  },
}));

// 毛玻璃效果的选择框 - 全白色文字
const GlassSelect = styled(Select)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  borderRadius: "12px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.2)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.3)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255, 255, 255, 0.4)",
  },
  "& .MuiSelect-select": {
    color: "#fff",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
    position: "relative",
    zIndex: 1,
    fontSize: "0.95rem",
  },
  "& .MuiInputLabel-root": {
    color: "#fff",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
    position: "relative",
    zIndex: 1,
    fontSize: "0.9rem",
  },
  "& .MuiPaper-root": {
    background: "rgba(25,25,25,0.97)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: "12px",
    marginTop: "8px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  "& .MuiMenuItem-root": {
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.15)",
    position: "relative",
    zIndex: 1,
    fontSize: "0.95rem",
    padding: "10px 16px",
    background: "none",
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
      color: "#fff",
    },
    "&.Mui-selected": {
      background: "rgba(33,150,243,0.18)",
      color: "#fff",
      "&:hover": {
        background: "rgba(33,150,243,0.22)",
        color: "#fff",
      },
    },
  },
}));

// 毛玻璃效果的表格 - 全白字，透明背景
const GlassTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    borderBottom: "1px solid rgba(255,255,255,0.13)",
    background: "transparent",
    color: "#fff",
    textShadow: "0 1px 3px rgba(0,0,0,0.2)",
    position: "relative",
    zIndex: 1,
    fontSize: "0.97rem",
    padding: theme.spacing(2),
    fontWeight: 400,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    background: "transparent",
    fontWeight: 600,
    color: "#fff",
    fontSize: "1.07rem",
  },
  // 去除hover效果
}));

// 毛玻璃效果的对话框
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(50, 50, 50, 0.96)",
    color: "#fff",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  },
  "& .MuiDialogTitle-root, & .MuiDialogContentText-root": {
    color: "#fff",
  },
}));

// 毛玻璃效果的加载遮罩
const LoadingBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "rgba(25, 25, 25, 0.7)",
  backdropFilter: "blur(4px)",
}));

// 分组标题
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#fff",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

// 标题容器
const TitleContainer = styled(Box)(({ theme }) => ({
  height: "48px",
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  "& .MuiTypography-root": {
    fontSize: "1.25rem",
    fontWeight: 600,
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.23)",
    position: "relative",
    zIndex: 1,
  },
}));

// 作者选择容器
const AuthorSelectContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

// 作者标签 - 透明毛玻璃
const AuthorChip = styled(Chip)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "8px",
  fontSize: "0.85rem",
  height: "28px",
  "& .MuiChip-deleteIcon": {
    color: "#fff",
    position: "relative",
    zIndex: 1,
    fontSize: "0.85rem",
    "&:hover": {
      color: "#ffffff",
    },
  },
}));

// 状态标签 - 透明毛玻璃
const StatusChip = styled(Chip)(({ theme, status }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "8px",
  fontSize: "0.85rem",
  height: "28px",
}));

// 类型标签 - 透明毛玻璃
const TypeChip = styled(Chip)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  color: "#fff",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "8px",
  fontSize: "0.85rem",
  height: "28px",
}));

const iconWhite = { color: "#fff" };

const VisionSpaceManagement = () => {
  const [visionSpaces, setVisionSpaces] = useState([]);
  const [ministryWorkers, setMinistryWorkers] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [date, setDate] = useState("");
  const [type, setType] = useState("");
  const [isCustomType, setIsCustomType] = useState(false);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaType, setMediaType] = useState("none");
  const [status, setStatus] = useState("upcoming");
  const [currentId, setCurrentId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);

  // 获取所有异象空间活动
  const fetchVisionSpaces = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/vision-space`);
      setVisionSpaces(response.data);
    } catch (err) {
      setError(`获取异象空间数据失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 获取所有已存在的类型（从数据库中）
  const getExistingTypes = () => {
    const types = visionSpaces.map((vs) => vs.type).filter(Boolean);
    const uniqueTypes = [...new Set(types)];
    return uniqueTypes.sort();
  };

  // 获取所有同工数据
  const fetchMinistryWorkers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/ministry-worker`);
      setMinistryWorkers(response.data);
    } catch (err) {
      setError(`获取同工数据失败: ${err.message}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [visionSpacesResponse, ministryWorkersResponse] =
          await Promise.all([
            axios.get(`${API_BASE_URL}/vision-space`),
            axios.get(`${API_BASE_URL}/ministry-worker`),
          ]);
        setVisionSpaces(visionSpacesResponse.data);
        setMinistryWorkers(ministryWorkersResponse.data);
      } catch (err) {
        setError("获取数据失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 重置表单
  const resetForm = () => {
    setTitle("");
    setContent("");
    setDescription("");
    setSelectedAuthors([]);
    setDate("");
    setType("");
    setIsCustomType(false);
    setMediaUrl("");
    setMediaType("none");
    setStatus("upcoming");
    setCurrentId(null);
    setIsEditing(false);
    setAudioFile(null);
    setAudioPreview(null);
  };

  const handleAudioChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
    }
  };

  // 处理媒体类型变化
  const handleMediaTypeChange = (e) => {
    const newMediaType = e.target.value;
    setMediaType(newMediaType);
    // 当媒体类型改变时，清空相关的媒体内容
    if (newMediaType === "none") {
      setMediaUrl("");
      setAudioFile(null);
      setAudioPreview(null);
    } else if (newMediaType === "video") {
      setAudioFile(null);
      setAudioPreview(null);
    } else if (newMediaType === "audio") {
      setMediaUrl("");
    }
  };

  // 处理编辑
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/vision-space/${id}`);
      const visionSpace = response.data;
      setTitle(visionSpace.title);
      setContent(visionSpace.content);
      setDescription(visionSpace.description);
      setSelectedAuthors(visionSpace.authors.map((author) => author._id));
      const localDate = new Date(visionSpace.date);
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, "0");
      const day = String(localDate.getDate()).padStart(2, "0");
      setDate(`${year}-${month}-${day}`);
      const existingTypes = getExistingTypes();
      if (existingTypes.includes(visionSpace.type)) {
        setType(visionSpace.type);
        setIsCustomType(false);
      } else {
        // 如果类型不在现有类型列表中，使用自定义类型输入框
        setIsCustomType(true);
        setType(visionSpace.type);
      }
      setMediaType(visionSpace.mediaType);

      if (visionSpace.mediaType === "video") {
        setMediaUrl(visionSpace.mediaUrl || "");
        setAudioPreview(null);
      } else if (visionSpace.mediaType === "audio") {
        setMediaUrl("");
        setAudioPreview(
          visionSpace.audioUrl ? `${API_BASE_URL}${visionSpace.audioUrl}` : null
        );
      } else {
        setMediaUrl("");
        setAudioPreview(null);
      }

      setStatus(visionSpace.status);
      setCurrentId(id);
      setIsEditing(true);
    } catch (err) {
      setError(`获取异象空间活动详情失败: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 处理日期变化
  const handleDateChange = (e) => {
    // 使用洛杉矶时区
    const laTimeZone = "America/Los_Angeles";
    // 解析选择的日期
    const [year, month, day] = e.target.value.split("-").map(Number);
    const selectedDate = new Date(year, month - 1, day);

    // 获取当前洛杉矶时间
    const now = new Date();
    const laNow = new Date(
      now.toLocaleString("en-US", { timeZone: laTimeZone })
    );

    // 重置时间为当天的开始时间
    selectedDate.setHours(0, 0, 0, 0);
    laNow.setHours(0, 0, 0, 0);

    // 判断日期状态
    let newStatus;
    if (selectedDate.getTime() === laNow.getTime()) {
      newStatus = "ongoing";
    } else if (selectedDate > laNow) {
      newStatus = "upcoming";
    } else {
      newStatus = "completed";
    }

    setStatus(newStatus);
    setDate(e.target.value);
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 验证类型
    if (!type || type.trim() === "") {
      setError("请输入类型");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let finalMediaUrl = "";
      let audioUrl = "";
      // 根据媒体类型处理不同的媒体内容
      if (mediaType === "video") {
        finalMediaUrl = mediaUrl;
      } else if (mediaType === "audio") {
        if (currentId) {
          const currentVisionSpace = visionSpaces.find(
            (vs) => vs._id === currentId
          );
          audioUrl = currentVisionSpace?.audioUrl || "";
        }
        if (audioFile) {
          const audioFormData = new FormData();
          audioFormData.append("audio", audioFile);
          const audioResponse = await axios.post(
            `${API_BASE_URL}/upload/audio`,
            audioFormData
          );
          audioUrl = audioResponse.data.url;
        }
      }
      // 处理日期，使用洛杉矶时区
      const laTimeZone = "America/Los_Angeles";
      const [year, month, day] = date.split("-").map(Number);
      const localDate = new Date(year, month - 1, day);
      localDate.setHours(12, 0, 0, 0);

      const data = {
        title,
        content,
        description,
        authors: selectedAuthors,
        date: localDate.toISOString(),
        type,
        mediaType,
        mediaUrl: mediaType === "video" ? finalMediaUrl : "",
        audioUrl: mediaType === "audio" ? audioUrl : "",
        status,
      };

      let response;
      if (currentId) {
        response = await axios.put(
          `${API_BASE_URL}/vision-space/${currentId}`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/vision-space`, data);
      }

      setSuccess(currentId ? "更新成功" : "创建成功");
      setCurrentId(null);
      resetForm();
      fetchVisionSpaces();
    } catch (error) {
      setError(
        error.response?.data?.message || (currentId ? "更新失败" : "创建失败")
      );
    } finally {
      setLoading(false);
    }
  };

  // 处理删除
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/vision-space/${deleteId}`);
      setSuccess("异象空间活动删除成功");
      fetchVisionSpaces();
    } catch (err) {
      setError(`删除失败: ${err.message}`);
    } finally {
      setLoading(false);
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#fff",
          textShadow: "0 1px 2px rgba(0,0,0,0.29)",
          position: "relative",
          zIndex: 1,
        }}
      >
        异象空间管理
      </Typography>

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            backgroundColor: "rgba(237, 247, 237, 0.6)",
            border: "1px solid rgba(46, 125, 50, 0.1)",
            borderRadius: "8px",
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            "& .MuiAlert-message": { color: "#fff" },
            "& .MuiAlert-icon": { color: "#fff" },
            backgroundColor: "rgba(244, 67, 54, 0.2)",
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 表单部分 */}
        <Grid item xs={12} md={4}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                {isEditing ? "编辑异象空间活动" : "创建异象空间活动"}
              </Typography>

              <form onSubmit={handleSubmit}>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: "#fff" }}>类型</InputLabel>
                  <GlassSelect
                    value={isCustomType ? "其他" : type}
                    label="类型"
                    onChange={(e) => {
                      if (e.target.value === "其他") {
                        setIsCustomType(true);
                        setType("");
                      } else {
                        setIsCustomType(false);
                        setType(e.target.value);
                      }
                    }}
                    renderValue={(selected) => (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <EventIcon sx={iconWhite} />
                        <Typography sx={{ color: "#fff" }}>
                          {isCustomType ? type || "请输入类型" : selected}
                        </Typography>
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "rgba(25,25,25,0.96)",
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    {getExistingTypes().map((existingType) => (
                      <MenuItem
                        key={existingType}
                        value={existingType}
                        sx={{ color: "#fff" }}
                      >
                        {existingType}
                      </MenuItem>
                    ))}
                    <MenuItem value="其他" sx={{ color: "#fff" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography color="#fff">其他</Typography>
                        <Typography variant="caption" color="#fff">
                          (输入新类型)
                        </Typography>
                      </Box>
                    </MenuItem>
                  </GlassSelect>
                </FormControl>

                {isCustomType && (
                  <GlassTextField
                    fullWidth
                    label="输入新类型"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    sx={{ mb: 3 }}
                    placeholder="例如：福音漫谈"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EventIcon sx={iconWhite} />
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{ sx: { color: "#fff" } }}
                  />
                )}

                <GlassTextField
                  fullWidth
                  label="标题"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon sx={iconWhite} />
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{ sx: { color: "#fff" } }}
                />

                <AuthorSelectContainer>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: "#fff" }}>选择作者</InputLabel>
                    <GlassSelect
                      multiple
                      value={selectedAuthors}
                      onChange={(e) => setSelectedAuthors(e.target.value)}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const worker = ministryWorkers.find(
                              (w) => w._id === value
                            );
                            return (
                              <AuthorChip
                                key={value}
                                label={worker ? worker.name : value}
                                onDelete={() => {
                                  setSelectedAuthors(
                                    selectedAuthors.filter((id) => id !== value)
                                  );
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            background: "rgba(25,25,25,0.96)",
                            color: "#fff",
                          },
                        },
                      }}
                    >
                      {ministryWorkers.map((worker) => (
                        <MenuItem
                          key={worker._id}
                          value={worker._id}
                          sx={{ color: "#fff" }}
                        >
                          {worker.name}
                        </MenuItem>
                      ))}
                    </GlassSelect>
                  </FormControl>
                </AuthorSelectContainer>

                <GlassTextField
                  fullWidth
                  label="内容"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  multiline
                  rows={4}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon sx={iconWhite} />
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{ sx: { color: "#fff" } }}
                />

                <GlassTextField
                  fullWidth
                  label="描述"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InfoOutlinedIcon sx={iconWhite} />
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{ sx: { color: "#fff" } }}
                />

                <GlassTextField
                  fullWidth
                  label="日期"
                  type="date"
                  value={date}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true, sx: { color: "#fff" } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon sx={iconWhite} />
                      </InputAdornment>
                    ),
                  }}
                  FormHelperTextProps={{ sx: { color: "#fff" } }}
                />

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: "#fff" }}>媒体类型</InputLabel>
                  <GlassSelect
                    value={mediaType}
                    label="媒体类型"
                    onChange={handleMediaTypeChange}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "rgba(25,25,25,0.96)",
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    <MenuItem value="none" sx={{ color: "#fff" }}>
                      无
                    </MenuItem>
                    <MenuItem value="video" sx={{ color: "#fff" }}>
                      视频
                    </MenuItem>
                    <MenuItem value="audio" sx={{ color: "#fff" }}>
                      音频
                    </MenuItem>
                  </GlassSelect>
                </FormControl>

                {mediaType === "video" && (
                  <GlassTextField
                    fullWidth
                    label="YouTube视频链接"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <VideocamIcon sx={iconWhite} />
                        </InputAdornment>
                      ),
                    }}
                    FormHelperTextProps={{ sx: { color: "#fff" } }}
                  />
                )}

                {mediaType === "audio" && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, fontWeight: 500, color: "#fff" }}
                    >
                      音频文件
                    </Typography>
                    <input
                      accept="audio/*"
                      style={{ display: "none" }}
                      id="audio-upload"
                      type="file"
                      onChange={handleAudioChange}
                    />
                    <label htmlFor="audio-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<MusicNoteIcon sx={iconWhite} />}
                        sx={{
                          mr: 2,
                          color: "#fff",
                          borderColor: "rgba(255,255,255,0.65)",
                        }}
                      >
                        选择音频文件
                      </Button>
                    </label>
                    {audioPreview && (
                      <Box sx={{ mt: 2 }}>
                        <audio
                          controls
                          src={audioPreview}
                          style={{ width: "100%" }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel sx={{ color: "#fff" }}>状态</InputLabel>
                  <GlassSelect
                    value={status}
                    label="状态"
                    onChange={(e) => setStatus(e.target.value)}
                    disabled={date && new Date(date) > new Date()}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: "rgba(25,25,25,0.96)",
                          color: "#fff",
                        },
                      },
                    }}
                  >
                    <MenuItem value="upcoming" sx={{ color: "#fff" }}>
                      即将开始
                    </MenuItem>
                    <MenuItem value="ongoing" sx={{ color: "#fff" }}>
                      正在进行
                    </MenuItem>
                    <MenuItem value="completed" sx={{ color: "#fff" }}>
                      已完成
                    </MenuItem>
                  </GlassSelect>
                </FormControl>

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  {isEditing && (
                    <GlassButton
                      variant="outlined"
                      onClick={resetForm}
                      startIcon={<AddIcon sx={iconWhite} />}
                      color="#4caf50"
                    >
                      新建
                    </GlassButton>
                  )}
                  <GlassButton
                    type="submit"
                    variant="contained"
                    startIcon={
                      isEditing ? (
                        <SaveIcon sx={iconWhite} />
                      ) : (
                        <AddIcon sx={iconWhite} />
                      )
                    }
                    disabled={loading}
                    color="#2196f3"
                  >
                    {isEditing ? "更新" : "创建"}
                  </GlassButton>
                </Box>
              </form>
            </Box>
          </GlassCard>
        </Grid>

        {/* 列表部分 */}
        <Grid item xs={12} md={8}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                异象空间活动列表
              </Typography>

              {visionSpaces.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#fff",
                      textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    }}
                  >
                    暂无异象空间数据
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <GlassTable>
                    <TableHead>
                      <TableRow>
                        <TableCell>标题</TableCell>
                        <TableCell>类型</TableCell>
                        <TableCell>作者</TableCell>
                        <TableCell>日期</TableCell>
                        <TableCell>状态</TableCell>
                        <TableCell>操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visionSpaces.map((visionSpace) => (
                        <TableRow key={visionSpace._id}>
                          <TableCell>{visionSpace.title}</TableCell>
                          <TableCell>
                            <TypeChip label={visionSpace.type} />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {visionSpace.authors.map((author) => (
                                <AuthorChip
                                  key={author._id}
                                  label={author.name}
                                  size="small"
                                />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {new Date(visionSpace.date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <StatusChip
                              status={visionSpace.status}
                              label={
                                visionSpace.status === "upcoming"
                                  ? "即将开始"
                                  : visionSpace.status === "ongoing"
                                  ? "正在进行"
                                  : "已完成"
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(visionSpace._id)}
                              sx={{ color: "#fff" }}
                            >
                              <EditIcon fontSize="small" sx={iconWhite} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenDeleteDialog(visionSpace._id)
                              }
                              sx={{ color: "#fff" }}
                            >
                              <DeleteIcon fontSize="small" sx={iconWhite} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </GlassTable>
                </TableContainer>
              )}
            </Box>
          </GlassCard>
        </Grid>
      </Grid>

      {/* 删除确认对话框 */}
      <GlassDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ color: "#fff" }}>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#fff" }}>
            您确定要删除这个异象空间活动吗？此操作无法撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ color: "#fff" }}>
            取消
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            sx={{ color: "#fff" }}
          >
            删除
          </Button>
        </DialogActions>
      </GlassDialog>

      {/* 加载中遮罩 */}
      <LoadingBackdrop open={loading}>
        <CircularProgress sx={{ color: "#fff" }} />
      </LoadingBackdrop>
    </Box>
  );
};

export default VisionSpaceManagement;
